/**
 * Frontend logger utility with environment-aware logging
 * Only logs debug messages in development mode
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LoggerOptions {
  context?: string;
  data?: any;
}

class Logger {
  private isDevelopment: boolean;
  private isTest: boolean;

  constructor() {
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isTest = process.env.NODE_ENV === 'test';
  }

  private formatMessage(level: LogLevel, message: string, options?: LoggerOptions): string {
    const timestamp = new Date().toISOString();
    const context = options?.context ? `[${options.context}]` : '';
    return `[${timestamp}] ${level.toUpperCase()} ${context} ${message}`;
  }

  /**
   * Log debug messages (only in development)
   */
  debug(message: string, options?: LoggerOptions): void {
    if (!this.isDevelopment || this.isTest) return;

    const formatted = this.formatMessage('debug', message, options);
    console.debug(formatted);
    if (options?.data !== undefined) {
      console.debug('Data:', options.data);
    }
  }

  /**
   * Log info messages (only in development)
   */
  info(message: string, options?: LoggerOptions): void {
    if (!this.isDevelopment || this.isTest) return;

    const formatted = this.formatMessage('info', message, options);
    console.log(formatted);
    if (options?.data !== undefined) {
      console.log('Data:', options.data);
    }
  }

  /**
   * Log warning messages (all environments)
   */
  warn(message: string, options?: LoggerOptions): void {
    if (this.isTest) return;

    const formatted = this.formatMessage('warn', message, options);
    console.warn(formatted);
    if (options?.data !== undefined) {
      console.warn('Data:', options.data);
    }
  }

  /**
   * Log error messages (all environments)
   */
  error(message: string, error?: Error | any, options?: LoggerOptions): void {
    if (this.isTest) return;

    const formatted = this.formatMessage('error', message, options);
    console.error(formatted);

    if (error) {
      if (error instanceof Error) {
        console.error('Error:', error.message);
        if (this.isDevelopment && error.stack) {
          console.error('Stack:', error.stack);
        }
      } else {
        console.error('Error details:', error);
      }
    }

    if (options?.data !== undefined) {
      console.error('Data:', options.data);
    }
  }

  /**
   * Create a child logger with a specific context
   */
  withContext(context: string): ContextLogger {
    return new ContextLogger(this, context);
  }
}

class ContextLogger {
  constructor(private parent: Logger, private context: string) {}

  debug(message: string, data?: any): void {
    this.parent.debug(message, { context: this.context, data });
  }

  info(message: string, data?: any): void {
    this.parent.info(message, { context: this.context, data });
  }

  warn(message: string, data?: any): void {
    this.parent.warn(message, { context: this.context, data });
  }

  error(message: string, error?: Error | any, data?: any): void {
    this.parent.error(message, error, { context: this.context, data });
  }
}

// Export singleton instance
const logger = new Logger();

export default logger;

// Named exports for convenience
export const { debug, info, warn, error } = {
  debug: logger.debug.bind(logger),
  info: logger.info.bind(logger),
  warn: logger.warn.bind(logger),
  error: logger.error.bind(logger),
};
